import { Field, Formik } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { config } from '../../../../utils/config';
import getValidator from '../../../../utils/getValidator';
import Button from '../../../atoms/Button';
import { useCityCompletion } from '../../../fields/ZipCodeCityField';
import SelectField from '../../../formik/SelectField';
import TextField from '../../../formik/TextField';
import './style.scss';

export type CollectionPointSearchParams = {
  streetHouseNumber: string;
  zipCode: string;
  city: string;
  maximumDistance: number;
};

type SearchProps = {
  onSearch: (searchParams: CollectionPointSearchParams) => Promise<void>;
  onSearchEdit: () => void;
  isEditing: boolean;
  storedSearch: CollectionPointSearchParams;
  isSearching?: boolean;
};

const Search = (props: SearchProps) => {
  const { onSearch, onSearchEdit, isEditing, storedSearch } = props;
  const { formatMessage } = useIntl();
  const getCity = useCityCompletion(config.country);
  const { zipCode: zipCodeConfig } = config.addressForm[config.country].zipCodeCity;

  const handleZipCodeBlur = (zipCodeValue: string, errors: any, setFieldValue: (key: string, val: string) => void) => {
    if (!(zipCodeValue && !errors)) {
      return;
    }

    const suggestedCity = getCity(zipCodeValue);
    if (suggestedCity) {
      setFieldValue('city', suggestedCity);
    }
  };

  return (
    <Formik initialValues={storedSearch} onSubmit={onSearch} enableReinitialize>
      {({ submitForm, values, errors, setFieldValue }) => (
        <div className="search">
          {!isEditing ? (
            <div className="search__stored">
              <p className="search__stored__details">
                {storedSearch.streetHouseNumber} &bull; {storedSearch.zipCode} &bull; {storedSearch.city}
              </p>
              <Button
                type="button"
                className="search__stored__button"
                onClick={onSearchEdit}
                data-test-id="shipping-pickup-search-edit"
              >
                <FormattedMessage id="generic.edit" />
              </Button>
            </div>
          ) : (
            <div className="form">
              <div className="form__row">
                <div className="form__field-container" style={{ flex: 2 }}>
                  <Field
                    name="streetHouseNumber"
                    component={TextField}
                    label={formatMessage({ id: 'address.streetHouseNumber' })}
                    maxLength={60}
                    data-test-id="shipping-collection-point-address"
                    validate={getValidator({ required: true })}
                  />
                </div>
                <div className="form__field-container">
                  <Field
                    onBlur={() => handleZipCodeBlur(values.zipCode, errors.zipCode, setFieldValue)}
                    name="zipCode"
                    component={TextField}
                    label={formatMessage({ id: 'address.zipCode' })}
                    maxLength={zipCodeConfig.maxLength ?? 8}
                    data-test-id="shipping-collection-point-zipcode"
                    validate={getValidator({ required: true, minLength: 3, format: 'default', ...zipCodeConfig })}
                  />
                </div>
              </div>
              <div className="form__row">
                <div className="form__field-container">
                  <Field
                    name="city"
                    component={TextField}
                    label={formatMessage({ id: 'address.city' })}
                    maxLength={50}
                    data-test-id="shipping-collection-point-city"
                    validate={getValidator({ required: true })}
                  />
                </div>
                <div className="form__field-container address-form__top-dropdown">
                  <Field
                    name="maximumDistance"
                    component={SelectField}
                    label={formatMessage({ id: 'shipping.collectionPoint.search.maximumDistance' })}
                    options={config.collectionPoints.maximumDistanceOptions.map(distanceInKm => ({
                      value: distanceInKm * 1000,
                      label: formatMessage({ id: 'shipping.collectionPoint.location.distance' }, { distanceInKm }),
                    }))}
                    validate={getValidator({ required: true })}
                  />
                </div>
                <div className="form__field-container hide-mobile">
                  <Button
                    type="button"
                    secondary
                    onClick={() => submitForm()}
                    disabled={props.isSearching}
                    isLoading={props.isSearching}
                    data-test-id="shipping-collection-point-search"
                  >
                    <FormattedMessage id="generic.search" />
                  </Button>
                </div>
              </div>
              <div className="form__field-container show-mobile">
                <Button
                  type="button"
                  secondary
                  onClick={() => submitForm()}
                  disabled={props.isSearching}
                  isLoading={props.isSearching}
                  data-test-id="shipping-collection-point-search"
                >
                  <FormattedMessage id="generic.search" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Formik>
  );
};

export default Search;
